import { FormEvent, useCallback, useEffect, useReducer, useState } from "react";
import { FirebaseError, initializeApp } from "firebase/app";

import {
  AuthContext,
  AuthOptions,
  AuthProviderProps,
  authReducer,
  Dispatch,
  initialState,
  SignupProps,
} from "./auth-context";

import {
  Auth,
  AuthErrorCodes,
  AuthProvider,
  createUserWithEmailAndPassword,
  EmailAuthCredential,
  EmailAuthProvider,
  getAuth,
  getRedirectResult,
  GithubAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  multiFactor,
  MultiFactorResolver,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  SAMLAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  updatePassword,
  User,
  UserCredential,
} from "firebase/auth";

import { handleErrorMessage } from "./lib/error-messages";
import { GeolockDialog } from "~/components/geolock-dialog";
import { passwordStrength } from "~/lib/password-strength";
import { Config } from "~/constants/configuration_provider";
import { useIncidentBanner } from "~/components/IncidentBanner/useIncidentBanner";
import { checkMFAStatus } from "./lib/check-mfa-status";
import { reauthenticateUser } from "./lib/reauthenticate-user";
import { getResolverFromError } from "./lib/get-resolver-from-error";

// Configure Firebase
const firebaseConfig = {
  apiKey: `${Config.VITE_FIREBASE_API_KEY}`,
  authDomain: `${Config.VITE_FIREBASE_AUTH_DOMAIN}`,
};

// Initialize the firebase application
initializeApp(firebaseConfig);

// Get the auth object from firebase
const auth: Auth = getAuth();

export * from "firebase/auth";
export { auth };

type AuthProviders = GoogleAuthProvider | GithubAuthProvider | OAuthProvider;

function getProvider(providerOption: AuthOptions): AuthProviders {
  switch (providerOption) {
    case "google":
      return new GoogleAuthProvider();
    case "github":
      return new GithubAuthProvider();
    case "msft":
      return new OAuthProvider("microsoft.com");
    default:
      throw new Error(`Not a recognized provider type: ${providerOption}`);
  }
}

export function FirebaseAuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(
    null,
  );
  const [hasFetchedUser, setHasFetchedUser] = useState<boolean>(false);
  const { incident, showIncidentBanner, onCloseIncidentBanner } =
    useIncidentBanner();

  const signInWithProvider = async (auth: Auth, provider: AuthProvider) => {
    return signInWithPopup(auth, provider);
  };

  const linkPendingEmailCredential = async (
    result: UserCredential,
    pendingEmailCredential: EmailAuthCredential,
  ) => {
    let pendingAuthMsg = {
      message: "Successfully connected email & password account.",
      variant: "success",
    };
    try {
      await linkWithCredential(result.user, pendingEmailCredential);
      window.location.reload();
    } catch (error) {
      let message = "Failed to connect email & password account.";
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case AuthErrorCodes.EMAIL_EXISTS:
          case AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE:
            message = message + " Email is already in use.";
        }
      }
      // SnackbarProvider is unavailable within AuthProvider. Find a new approach.
      // enqueueSnackbar(message, { variant: "error" });
      pendingAuthMsg = { message, variant: "error" };
    } finally {
      window.sessionStorage.setItem(
        "pendingAuthMessage",
        JSON.stringify(pendingAuthMsg),
      );
    }
  };

  useEffect(() => {
    try {
      getRedirectResult(auth)
        .then(async (result) => {
          if (result?.operationType === "link") {
            window.sessionStorage.setItem(
              "pendingAuthMessage",
              JSON.stringify({
                message: "Successfully connected account.",
                variant: "success",
              }),
            );
          }
          if (result?.operationType === "reauthenticate") {
            const pendingEmailCredential = EmailAuthCredential.fromJSON(
              window.sessionStorage.getItem("pendingEmailCredential") || "null",
            );
            if (pendingEmailCredential) {
              await linkPendingEmailCredential(result, pendingEmailCredential);
              window.sessionStorage.removeItem("pendingEmailCredential");
            }
          }
        })
        .catch((error) => {
          console.error("redirect result error", error);
          let message = "Error";
          if (error instanceof FirebaseError) {
            switch (error.code) {
              case AuthErrorCodes.EMAIL_EXISTS:
                message = "Failed to connect account. Email is already in use.";
              case AuthErrorCodes.CREDENTIAL_ALREADY_IN_USE:
                message =
                  "Failed to connect account. Account is already in use.";
            }
          }
          window.sessionStorage.setItem(
            "pendingAuthMessage",
            JSON.stringify({ message, variant: "error" }),
          );
        });

      onAuthStateChanged(auth, async (user: User | null) => {
        let sessionTimeout: ReturnType<typeof setTimeout> | null = null;

        if (user) {
          // Successful log in
          const idTokenResult = await user.getIdTokenResult();

          // Enforce maximum session duration if it's specified.
          if (Config.VITE_SESSION_MAX_DURATION) {
            const sessionMaxDuration = parseInt(
              Config.VITE_SESSION_MAX_DURATION,
            );
            const sessionStartTime = new Date(idTokenResult.authTime).getTime();
            const sessionTimeUsed = Date.now() - sessionStartTime;
            const sessionTimeLeft = Math.max(
              0,
              sessionMaxDuration - sessionTimeUsed,
            );
            const sessionInvalidatedAt = new Date(Date.now() + sessionTimeLeft);
            console.info(
              "Max session duration is %sms. Session will be invalidated at: %s",
              sessionMaxDuration,
              sessionInvalidatedAt,
            );
            sessionTimeout = setTimeout(() => auth.signOut(), sessionTimeLeft);
          }

          dispatch({ type: "set user success", user, idTokenResult });
        } else {
          // Clear out the sessionTimeout if it exists.
          if (sessionTimeout !== null) {
            clearTimeout(sessionTimeout);
            sessionTimeout = null;
          }

          // Successful log out
          dispatch({
            type: "set user success",
            user: null,
            idTokenResult: null,
          });
        }
        setHasFetchedUser(true);
      });
    } catch (error) {
      // auth failure
      console.error("auth failure", error);
      const message = handleErrorMessage(error);
      dispatch({ type: "set user failure", error: message });
    }
  }, []);

  const loginWithProvider = async (
    event: FormEvent,
    dispatch: Dispatch,
    providerOption: AuthOptions,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
    const provider = getProvider(providerOption);
    try {
      await signInWithProvider(auth, provider);
    } catch (error) {
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.MFA_REQUIRED
      ) {
        const resolver = getResolverFromError(auth, error);
        setMfaResolver(resolver);
        dispatch({ type: "mfa required", status: "mfa required" });
      } else {
        console.log(
          "%c---- ERROR LOGGING IN WITH PROVIDER",
          "background: pink; color: black",
        );
        const message = handleErrorMessage(error);
        dispatch({ type: "set user failure", error: message });
      }
    }
  };

  const loginWithSSO = async (
    event: FormEvent,
    dispatch: Dispatch,
    orgId: string,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
    try {
      // question for DOM do we need this for any particular reason?
      localStorage.setItem("saml.orgid", orgId);
      const provider = new SAMLAuthProvider(`saml.${orgId}`);
      await signInWithProvider(auth, provider);
    } catch (error) {
      console.log(
        "%c---- ERROR SIGNING IN WITH SSO",
        "background: pink; color: black",
      );
      const message = handleErrorMessage(error);
      dispatch({ type: "set user failure", error: message });
    }
  };

  const loginWithEmailAndPassword = async (
    event: FormEvent,
    dispatch: Dispatch,
    { email, password }: { email: string; password: string },
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      // Check if error is due to mfa requirement
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.MFA_REQUIRED
      ) {
        const resolver = getResolverFromError(auth, error);
        setMfaResolver(resolver);
        dispatch({ type: "mfa required", status: "mfa required" });
      } else {
        const message = handleErrorMessage(error);
        dispatch({ type: "set user failure", error: message });
      }
    }
  };

  const signupWithEmailAndPassword = async (
    event: FormEvent,
    dispatch: Dispatch,
    { email, password, optIn }: SignupProps,
  ) => {
    event.preventDefault();
    dispatch({ type: "set user begin" });

    await revalidatePassword(password);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      // get current user and set display name
      // await updateProfile(userCredential.user, {   <==== what do we want to do with this?
      //   displayName: `${firstName} ${lastName}`,
      // });
      // let subscriptions = [
      //   {
      //     listID: EmailPreferenceList.NotificationWeeklyReports,
      //     subscribe: true,
      //   },
      //   {
      //     listID: EmailPreferenceList.NotificationSpaceAlerts,
      //     subscribe: true,
      //   },
      // ];

      // if (optIn) {
      //   subscriptions.concat([
      //     { listID: EmailPreferenceList.NewsletterEvents, subscribe: true },
      //     { listID: EmailPreferenceList.NewsletterGeneral, subscribe: true },
      //     { listID: EmailPreferenceList.NewsletterProduct, subscribe: true },
      //   ]);
      // }
      // Apollo isn't yet hooked up
      // setEmailPreference({ variables: { input: subscriptions } });
    } catch (error) {
      console.log(
        "%c---- ERROR CREATING USER",
        "background: pink; color: black",
      );
      const message = handleErrorMessage(error);
      dispatch({ type: "set user failure", error: message });
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      location.reload();
    } catch (error) {
      console.log("%c---- ERROR LOGGING OUT", "background: pink; color: black");
      const message = handleErrorMessage(error);
      dispatch({ type: "set user failure", error: message });
    }
  };

  const sendResetPasswordEmail = async (
    dispatch: Dispatch,
    email: string,
    event?: FormEvent,
  ) => {
    if (event) {
      // we now send a password without a form event, so the event is optional
      event.preventDefault();
    }
    dispatch({ type: "reset password begin" });
    try {
      await sendPasswordResetEmail(auth, email);
      dispatch({ type: "reset password success" });
    } catch (error) {
      console.log(
        "%c---- ERROR RESETTING PASSWORD",
        "background: pink; color: black",
      );
      dispatch({ type: "reset password failure" });
    }
  };

  const sendUserEmailVerification = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        await sendEmailVerification(user);
      }
    } catch (error) {
      const message = handleErrorMessage(error);
      throw message;
    }
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string,
  ): Promise<void> => {
    const { user } = state;
    if (!user?.email) {
      return Promise.reject("Unable to update password");
    }
    const credential = EmailAuthProvider.credential(user.email, oldPassword);
    await reauthenticateWithCredential(user, credential);
    return updatePassword(user, newPassword);
  };

  const clearErrors = useCallback(() => {
    dispatch({ type: "clear errors" });
  }, []);

  const setGeolock = (isGeolocked: boolean) => {
    dispatch({ type: "set geolock", isGeolocked });
  };

  // We check again that our passwords are upheld to our chosen standard.
  // This must stay in sync with the checks set in signup.tsx
  const revalidatePassword = async (password: string) => {
    // Check against our chosen password length rules
    if (password.length > 100 || password.length < 8) {
      const message = "Your password must be between 8 and 100 characters";
      dispatch({ type: "set user failure", error: message });
      throw message;
    }

    // check again that we have a strict password score.
    const result = await passwordStrength(password);
    if (result.score < 3) {
      const suggestions = result.feedback.suggestions.join(" ");
      const warning = result.feedback.warning;
      const message = `Please use a more complex password. ${
        warning ? warning + "." : ""
      } ${suggestions}`;
      dispatch({ type: "set user failure", error: message });
      throw message;
    }
  };

  // MFA functions are passed through context
  const reauthenticateForMfA = async ({
    password,
  }: {
    password?: string;
  }): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }
    try {
      await reauthenticateUser({ user: auth.currentUser, password, dispatch });
    } catch (error) {
      // TODO: handle MFA errors here - see reauthenticate-for-mfa.ts
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.MFA_REQUIRED
      ) {
        const resolver = getResolverFromError(auth, error);
        setMfaResolver(resolver);
        return dispatch({ type: "mfa required", status: "mfa required" });
      }

      if (error instanceof FirebaseError) {
        const message = handleErrorMessage(error);
        throw new Error(message);
      }
    }
  };

  const generateMfaSecrets = async () => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }
    try {
      dispatch({
        type: "generate secrets begin",
        status: "pending secrets generation",
      });
      //Generate the TOTP Secret
      const multiFactorSession = await multiFactor(
        auth.currentUser,
      ).getSession();
      const totpSecret =
        await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      //Generate the QR code URL
      const accountName = auth.currentUser.email || undefined; // TODO - ok if this is undefined?  it should never be but you never know
      const issuer = "Mondoo";
      const qrCodeUrl = totpSecret.generateQrCodeUrl(accountName, issuer);
      dispatch({ type: "enroll mfa secret generated" });
      return {
        qrCodeUrl,
        secret: totpSecret,
      };
    } catch (error) {
      const message = handleErrorMessage(error);
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN
      ) {
        dispatch({
          type: "requires recent login",
          status: "requires recent login",
          error: "Password required for reauthentication",
        });
      } else {
        dispatch({
          type: "enroll mfa failure",
          status: "error",
          error: message,
        });
      }
      throw new Error(message);
    }
  };

  const enrollMfa = async ({
    enrollmentSecret,
    verificationCode,
  }: {
    enrollmentSecret: TotpSecret;
    verificationCode: string;
  }) => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }
    if (!enrollmentSecret || !verificationCode) {
      throw new Error("No enrollment secret or verification code provided.");
    }
    dispatch({ type: "enroll mfa begin", status: "pending mfa enrollment" });

    try {
      // Create the multi-factor assertion with the verification code
      const multiFactorAssertion =
        TotpMultiFactorGenerator.assertionForEnrollment(
          enrollmentSecret,
          // code from user generator
          verificationCode,
        );
      const displayName = "Authenticator App";
      // Finalize enrollment
      await multiFactor(auth.currentUser).enroll(
        multiFactorAssertion,
        displayName,
      );
      dispatch({
        type: "enroll mfa success",
        user: auth.currentUser,
        status: "idle",
      });
    } catch (error) {
      const message = handleErrorMessage(error);
      dispatch({ type: "enroll mfa failure", error: message, status: "error" });
      throw new Error(message);
    }
  };

  const verifyMfaCodeForLogin = async ({
    verificationCode,
  }: {
    verificationCode: string;
  }) => {
    if (!verificationCode) {
      throw new Error("No verification code provided.");
    }
    dispatch({ type: "mfa verification begin", status: "mfa verifying" });

    try {
      const resolver = mfaResolver;
      if (!resolver) {
        throw new Error("No MFA resolver available");
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        resolver.hints[0].uid,
        verificationCode,
      );

      const credential = await resolver.resolveSignIn(multiFactorAssertion);
      await auth.updateCurrentUser(credential.user);

      await auth.currentUser?.reload();
      setMfaResolver(null);
      dispatch({
        type: "mfa verification success",
        status: "idle",
      });
    } catch (error) {
      const message = handleErrorMessage(error);
      dispatch({
        type: "mfa verification failure",
        error: message,
        status: "mfa required",
      });
    }
  };

  const unenrollMfa = async ({ password }: { password?: string }) => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }
    const factors = multiFactor(auth.currentUser).enrolledFactors;
    //   we could really just grab factors[0] here since we only support one type of mfa
    const totpFactor = factors.find((factor) => factor.factorId === "totp");
    if (!totpFactor) {
      throw new Error("No MFA enrollment found for user");
    }

    try {
      dispatch({ type: "unenroll mfa begin", status: "pending" });
      await multiFactor(auth.currentUser).unenroll(totpFactor);
      dispatch({
        type: "unenroll mfa success",
        status: "idle",
        user: auth.currentUser,
      });
    } catch (error) {
      // if last login was too long ago, reauthentication is required
      if (
        error instanceof FirebaseError &&
        error.code === AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN
      ) {
        await reauthenticateForMfA({ password });
        return;
      }
      const message = handleErrorMessage(error);
      dispatch({
        type: "unenroll mfa failure",
        status: "error",
        error: message,
      });
      throw new Error(message);
    }
  };

  const verifyMfaCodeForUnenroll = async ({
    verificationCode,
  }: {
    verificationCode: string;
  }) => {
    if (!verificationCode) {
      throw new Error("No verification code provided.");
    }
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in.");
    }

    dispatch({ type: "mfa verification begin", status: "mfa verifying" });

    try {
      const resolver = mfaResolver;
      if (!resolver) {
        throw new Error("No MFA resolver available");
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        resolver.hints[0].uid,
        verificationCode,
      );
      const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
      await auth.updateCurrentUser(userCredential.user);
      await auth.currentUser?.reload();
      setMfaResolver(null);

      await auth.currentUser.reload();
      dispatch({
        type: "mfa verification success",
        user: auth.currentUser,
        status: "idle",
      });

      // Now proceed to unenroll
      const factors = multiFactor(auth.currentUser).enrolledFactors;
      //   we could really just grab factors[0] here since we only support one type of mfa
      const totpFactor = factors.find((factor) => factor.factorId === "totp");
      if (!totpFactor) {
        throw new Error("No MFA enrollment found for user");
      }
      await multiFactor(auth.currentUser).unenroll(totpFactor);
      dispatch({
        type: "unenroll mfa success",
        status: "idle",
        user: auth.currentUser,
      });
    } catch (error) {
      const message = handleErrorMessage(error);
      dispatch({
        type: "mfa verification failure",
        error: message,
        status: state.status,
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        logout,
        loginWithProvider,
        signupWithEmailAndPassword,
        loginWithEmailAndPassword,
        loginWithSSO,
        sendResetPasswordEmail,
        sendUserEmailVerification,
        changePassword,
        clearErrors,
        setGeolock,
        // MFA functions
        reauthenticateForMfA,
        generateMfaSecrets,
        enrollMfa,
        unenrollMfa,
        checkMFAStatus,
        verifyMfaCodeForLogin,
        verifyMfaCodeForUnenroll,
        handleErrorMessage,
        incident: {
          data: incident,
          showIncidentBanner,
          onCloseIncidentBanner,
        },
      }}
    >
      {hasFetchedUser && children}
      {state.isGeolocked && <GeolockDialog />}
    </AuthContext.Provider>
  );
}
